import {
  CheckIcon,
  ChevronDownIcon,
  Loader2Icon,
  SlidersHorizontalIcon,
  XIcon,
} from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import {
  CandidateCard,
  CandidateCardActions,
  CandidateCardContent,
  CandidateCardHeader,
  CandidateCardIdentity,
} from '@/modules/candidates/components/CandidateCard';
import CandidateScore from '@/modules/candidates/components/CandidateScore';
import { useJobQuery } from '@/modules/jobs/queries/useJobQuery';
import { CalibrationLog } from '@/modules/scout-sourcing/components/CalibrationLog';
import { IdealProfileSummary } from '@/modules/scout-sourcing/components/IdealProfileSummary';
import { ScoutPromptInput } from '@/modules/scout-sourcing/components/ScoutPromptInput';
import { TargetingProgress } from '@/modules/scout-sourcing/components/TargetingProgress';
import { TargetingRound } from '@/modules/scout-sourcing/components/TargetingRound';
import { scoutDefaults } from '@/modules/scout-sourcing/constants';
import { useAbandonTargetingMutation } from '@/modules/scout-sourcing/mutations/useAbandonTargetingMutation';
import { useConfirmTargetingMutation } from '@/modules/scout-sourcing/mutations/useConfirmTargetingMutation';
import { useNextTargetingRoundMutation } from '@/modules/scout-sourcing/mutations/useNextTargetingRoundMutation';
import { usePreviewCandidatesMutation } from '@/modules/scout-sourcing/mutations/usePreviewCandidatesMutation';
import { usePreviewChampionQueryMutation } from '@/modules/scout-sourcing/mutations/usePreviewChampionQueryMutation';
import { usePreviewMarketSizeMutation } from '@/modules/scout-sourcing/mutations/usePreviewMarketSizeMutation';
import { useStartScoutMutation } from '@/modules/scout-sourcing/mutations/useStartScoutMutation';
import { useScoutTargetingSessionQuery } from '@/modules/scout-sourcing/queries/useScoutTargetingSessionQuery';
import { ScoutApiError } from '@/modules/scout-sourcing/scoutClient';
import type {
  PersonaVoteRecord,
  ScoutCalibrationLog,
  ScoutChampionFilterChip,
  ScoutChampionFilters,
  ScoutIdealProfile,
  ScoutInquiry,
  ScoutLocationTarget,
  ScoutLocationType,
  ScoutMarketSizeResponse,
  ScoutMarketStatus,
  ScoutNextRoundResponse,
  ScoutPreviewCandidatesResponse,
  ScoutPreviewChampionQueryResponse,
  ScoutSearchCriteria,
  ScoutTargetingPersona,
} from '@/modules/scout-sourcing/types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/modules/ui/components/alert-dialog/AlertDialog';
import { Badge } from '@/modules/ui/components/badge/Badge';
import { Button } from '@/modules/ui/components/button/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/modules/ui/components/card/Card';
import Container from '@/modules/ui/components/container/Container';
import { Input } from '@/modules/ui/components/input/Input';
import { Label } from '@/modules/ui/components/label/Label';
import { PageHeader } from '@/modules/ui/components/page-header/PageHeader';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/modules/ui/components/select/Select';
import { Separator } from '@/modules/ui/components/separator/Separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/modules/ui/components/sheet/Sheet';
import { Subheading } from '@/modules/ui/components/subheading/Subheading';
import { TagInput } from '@/modules/ui/components/tag-input/TagInput';

type ScoutResumeState = {
  jobSourcingId: string;
  firstRound: ScoutNextRoundResponse;
};

function previewFiltersErrorMessage(error: unknown) {
  if (error instanceof ScoutApiError && error.status === 400) {
    return 'Scout could not prepare filters from this profile. Start over or adjust the brief and try again.';
  }
  return 'Scout could not prepare filters right now. Retry filters or start over.';
}

type ChampionFilterDraft = ScoutSearchCriteria;

interface MarketSnapshot {
  marketSize: number;
  marketStatus: ScoutMarketStatus;
  targetMin: number;
  targetMax: number;
  appliedAdjustments: string[];
  championQueries: string[];
  searchCriteria: ScoutSearchCriteria;
}

const EMPTY_FILTER_DRAFT: ChampionFilterDraft = {
  baseTitle: '',
  titleVariants: [],
  locations: [],
  seniority: '',
  managementLevel: '',
  headlineKeywords: [],
  excludedTitles: [],
  skills: [],
  skillMatchMode: 'any',
  languages: [],
};

function normalizeLabel(value: string) {
  return value.trim().toLowerCase();
}

function valuesFromFilterItem(
  filters: ScoutChampionFilters | null | undefined,
  groupId: string,
  labels: string[],
) {
  const group = filters?.groups.find((item) => item.id === groupId);
  const wanted = labels.map(normalizeLabel);
  const item = group?.items.find((candidate) =>
    wanted.some((label) => normalizeLabel(candidate.label).includes(label)),
  );
  if (!item) return [];
  return Array.isArray(item.value)
    ? item.value.filter(Boolean)
    : item.value
      ? [item.value]
      : [];
}

function firstValue(values: string[]) {
  return values[0] ?? '';
}

const LOCATION_TYPE_OPTIONS = [
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'country', label: 'Country' },
  { value: 'region', label: 'Region' },
] satisfies { value: ScoutLocationType; label: string }[];

function locationTypeLabel(type: ScoutLocationType) {
  return (
    LOCATION_TYPE_OPTIONS.find((option) => option.value === type)?.label ?? type
  );
}

function isLocationType(value: string): value is ScoutLocationType {
  return LOCATION_TYPE_OPTIONS.some((option) => option.value === value);
}

function inquiryLocations(inquiry: ScoutInquiry | null) {
  return inquiry?.locations?.length
    ? uniqueLocationTargets(inquiry.locations)
    : [];
}

function locationTargetKey(location: ScoutLocationTarget) {
  return JSON.stringify([
    normalizeLabel(location.type),
    normalizeLabel(location.value),
    normalizeLabel(location.countryIso2 ?? ''),
  ]);
}

function formatLocationTarget(location: ScoutLocationTarget) {
  const details = [
    locationTypeLabel(location.type),
    normalizeLabel(location.value) !== normalizeLabel(location.displayName)
      ? location.value
      : null,
    location.countryIso2?.toUpperCase() || null,
  ].filter(Boolean);
  return `${location.displayName} · ${details.join(' · ')}`;
}

function normalizeLocationTarget(
  location: ScoutLocationTarget,
): ScoutLocationTarget {
  const countryIso2 = location.countryIso2?.trim().toUpperCase() || null;
  return {
    displayName: location.displayName.trim(),
    type: location.type,
    value: location.value.trim(),
    countryIso2,
  };
}

function uniqueLocationTargets(locations: ScoutLocationTarget[]) {
  const seen = new Set<string>();
  return locations.flatMap((location) => {
    const normalized = normalizeLocationTarget(location);
    const key = locationTargetKey(normalized);
    if (seen.has(key)) return [];
    seen.add(key);
    return [normalized];
  });
}

function withSearchCriteriaDefaults(
  criteria: ScoutSearchCriteria,
): ScoutSearchCriteria {
  return {
    ...criteria,
    locations: uniqueLocationTargets(criteria.locations ?? []),
    skillMatchMode: criteria.skillMatchMode ?? 'any',
  };
}

function normalizeSearchCriteria(criteria: ScoutSearchCriteria) {
  const normalizedCriteria = withSearchCriteriaDefaults(criteria);
  return JSON.stringify({
    baseTitle: normalizedCriteria.baseTitle,
    titleVariants: normalizedCriteria.titleVariants,
    locations: normalizedCriteria.locations.map((location) => ({
      displayName: location.displayName,
      type: location.type,
      value: location.value,
      countryIso2: location.countryIso2 ?? null,
    })),
    seniority: normalizedCriteria.seniority,
    managementLevel: normalizedCriteria.managementLevel,
    headlineKeywords: normalizedCriteria.headlineKeywords,
    excludedTitles: normalizedCriteria.excludedTitles,
    skills: normalizedCriteria.skills,
    skillMatchMode: normalizedCriteria.skillMatchMode,
    languages: normalizedCriteria.languages,
  });
}

function resolveMarketStatus(
  marketSize: number,
  targetMin: number,
  targetMax: number,
  marketStatus?: ScoutMarketStatus | null,
): ScoutMarketStatus {
  if (marketStatus) return marketStatus;
  if (marketSize <= 0) return 'no_market';
  if (marketSize < targetMin) return 'below_target';
  if (marketSize > targetMax) return 'above_target';
  return 'in_range';
}

function marketSnapshotFromPreview(
  preview: ScoutPreviewChampionQueryResponse,
  inquiry: ScoutInquiry | null,
): MarketSnapshot {
  const targetMin = preview.targetMin ?? preview.calibrationLog.targetMin ?? 50;
  const targetMax =
    preview.targetMax ?? preview.calibrationLog.targetMax ?? 300;
  const searchCriteria = filterDraftFromPreview(preview, inquiry);
  return {
    marketSize: preview.marketSize,
    marketStatus: resolveMarketStatus(
      preview.marketSize,
      targetMin,
      targetMax,
      preview.marketStatus,
    ),
    targetMin,
    targetMax,
    appliedAdjustments: preview.appliedAdjustments ?? [],
    championQueries: preview.championQueries,
    searchCriteria,
  };
}

function marketSnapshotFromResponse(
  response: ScoutMarketSizeResponse,
  requestedCriteria: ScoutSearchCriteria,
  fallback: MarketSnapshot,
): MarketSnapshot {
  const targetMin = response.targetMin ?? fallback.targetMin;
  const targetMax = response.targetMax ?? fallback.targetMax;
  return {
    marketSize: response.marketSize,
    marketStatus: resolveMarketStatus(
      response.marketSize,
      targetMin,
      targetMax,
      response.marketStatus,
    ),
    targetMin,
    targetMax,
    appliedAdjustments: response.appliedAdjustments ?? [],
    championQueries: response.championQueries,
    searchCriteria: withSearchCriteriaDefaults(
      response.searchCriteria ?? requestedCriteria,
    ),
  };
}

function splitCommaValues(value: string | undefined) {
  return (value ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function filterDraftFromPreview(
  preview: ScoutPreviewChampionQueryResponse,
  inquiry: ScoutInquiry | null,
): ChampionFilterDraft {
  if (preview.searchCriteria) {
    return withSearchCriteriaDefaults(preview.searchCriteria);
  }

  const filters = preview.championFilters;
  const baseTitle =
    firstValue(
      valuesFromFilterItem(filters, 'identity_location', ['current title']),
    ) ||
    inquiry?.baseTitle ||
    '';
  const titleVariants =
    valuesFromFilterItem(filters, 'identity_location', ['title variants']) ||
    [];
  const locations = inquiry?.locations?.length
    ? uniqueLocationTargets(inquiry.locations)
    : [];
  const headlineKeywords = valuesFromFilterItem(filters, 'identity_location', [
    'headline',
  ]).flatMap(splitCommaValues);
  const seniority =
    firstValue(
      valuesFromFilterItem(filters, 'role_seniority', ['seniority']),
    ) ||
    inquiry?.seniorityBand ||
    '';
  const managementLevel =
    firstValue(
      valuesFromFilterItem(filters, 'role_seniority', ['management']),
    ) ||
    inquiry?.seniorityBand ||
    '';
  const excludedTitles =
    valuesFromFilterItem(filters, 'role_seniority', ['excluded']) ||
    inquiry?.excludedTitles ||
    [];
  const skills = valuesFromFilterItem(filters, 'skills_languages', [
    'skills',
  ]).flatMap(splitCommaValues);
  const filterLanguages = valuesFromFilterItem(filters, 'skills_languages', [
    'languages',
  ]).flatMap(splitCommaValues);
  const languages =
    filterLanguages.length > 0
      ? filterLanguages
      : (inquiry?.languageRequirements ?? []);

  return {
    baseTitle,
    titleVariants,
    locations,
    seniority,
    managementLevel,
    headlineKeywords,
    excludedTitles,
    skills,
    skillMatchMode: 'any',
    languages,
  };
}

function legacyLocationDisplayFromPreview(
  preview: ScoutPreviewChampionQueryResponse,
  inquiry: ScoutInquiry | null,
) {
  if (inquiry?.locations?.length) return null;
  const inquiryLocation = inquiry?.location?.trim();
  if (inquiryLocation) return inquiryLocation;
  const filterLocations = valuesFromFilterItem(
    preview.championFilters,
    'identity_location',
    ['location'],
  )
    .map((location) => location.trim())
    .filter(Boolean);
  return filterLocations.length > 0 ? filterLocations.join(' · ') : null;
}

function chipsFromFilterDraft(
  draft: ChampionFilterDraft,
): ScoutChampionFilterChip[] {
  const chips: ScoutChampionFilterChip[] = [];
  const addChip = (label: string, value: string | string[]) => {
    const normalizedValue = Array.isArray(value)
      ? value.filter(Boolean).join(', ')
      : value.trim();
    if (!normalizedValue) return;
    chips.push({ label, value: normalizedValue });
  };

  addChip('Title', draft.baseTitle);
  addChip('Variants', draft.titleVariants);
  draft.locations.forEach((location) =>
    addChip('Location', formatLocationTarget(location)),
  );
  addChip('Seniority', draft.seniority);
  addChip('Management', draft.managementLevel);
  addChip('Headline', draft.headlineKeywords);
  addChip('Excluding', draft.excludedTitles);
  addChip('Skills', draft.skills);
  if (draft.skills.length > 1) {
    addChip('Skill match', draft.skillMatchMode === 'all' ? 'All' : 'Any');
  }
  addChip('Languages', draft.languages);

  return chips;
}

interface FilterDraftValidationIssue {
  field: string;
  value: string;
  message: string;
}

const HUMAN_CONNECTOR_RE = /[,;]|\b(or|and|similar|relevant)\b/i;
const WHOLE_SENTENCE_RE =
  /\b(also include|roughly|with experience|similar .+ stack)\b/i;
const GENERIC_TITLE_RE = /^(engineer|developer)$/i;

function validateFilterTerm({
  field,
  value,
  isTitle = false,
  allowAlternatives = false,
}: {
  field: string;
  value: string;
  isTitle?: boolean;
  allowAlternatives?: boolean;
}): FilterDraftValidationIssue | null {
  const normalizedValue = value.trim();
  if (!normalizedValue) return null;

  if (isTitle && normalizedValue.includes(',')) {
    return {
      field,
      value: normalizedValue,
      message: 'Use one title per tag.',
    };
  }

  if (isTitle && GENERIC_TITLE_RE.test(normalizedValue)) {
    return {
      field,
      value: normalizedValue,
      message: 'Use a more specific title.',
    };
  }

  if (
    WHOLE_SENTENCE_RE.test(normalizedValue) ||
    (!allowAlternatives &&
      HUMAN_CONNECTOR_RE.test(normalizedValue) &&
      normalizedValue.split(/\s+/).length >= 4)
  ) {
    return {
      field,
      value: normalizedValue,
      message: 'Split this into short, separate values.',
    };
  }

  return null;
}

function formatFilterDraftValidationMessage(
  issues: FilterDraftValidationIssue[],
) {
  const details = issues
    .slice(0, 3)
    .map((issue) => `${issue.field} "${issue.value}": ${issue.message}`)
    .join(' ');
  const suffix = issues.length > 3 ? ` +${issues.length - 3} more fields.` : '';
  return `Some filters are too broad for search. ${details}${suffix}`;
}

function validateFilterDraft(draft: ChampionFilterDraft) {
  const issues = [
    validateFilterTerm({
      field: 'Title',
      value: draft.baseTitle,
      isTitle: true,
    }),
    ...draft.titleVariants.map((value) =>
      validateFilterTerm({ field: 'Title variant', value, isTitle: true }),
    ),
    ...draft.locations.map((location) =>
      validateFilterTerm({
        field: 'Location',
        value: location.value,
      }),
    ),
    ...draft.locations
      .filter(
        (location) =>
          location.countryIso2 && !/^[A-Za-z]{2}$/.test(location.countryIso2),
      )
      .map((location) => ({
        field: 'Location ISO2',
        value: location.countryIso2 ?? '',
        message: 'Use a two-letter country code.',
      })),
    ...draft.headlineKeywords.map((value) =>
      validateFilterTerm({
        field: 'Headline keyword',
        value,
        allowAlternatives: true,
      }),
    ),
    validateFilterTerm({
      field: 'Management level',
      value: draft.managementLevel,
    }),
    ...draft.excludedTitles.map((value) =>
      validateFilterTerm({ field: 'Excluded title', value, isTitle: true }),
    ),
    ...draft.skills.map((value) =>
      validateFilterTerm({ field: 'Skill', value }),
    ),
    ...draft.languages.map((value) =>
      validateFilterTerm({ field: 'Language', value }),
    ),
  ].filter((issue): issue is FilterDraftValidationIssue => Boolean(issue));

  if (!draft.baseTitle.trim()) return 'Current title is required.';
  if (!draft.locations.length) return 'Add at least one allowed location.';
  if (
    draft.locations.some(
      (location) => !location.displayName.trim() || !location.value.trim(),
    )
  ) {
    return 'Every allowed location needs a name and search value.';
  }
  return issues.length ? formatFilterDraftValidationMessage(issues) : null;
}

function LocationTargetsEditor({
  value,
  legacyDisplayName,
  disabled,
  onChange,
}: {
  value: ScoutLocationTarget[];
  legacyDisplayName?: string | null;
  disabled: boolean;
  onChange: (locations: ScoutLocationTarget[]) => void;
}) {
  const id = useId();
  const [locationType, setLocationType] = useState<ScoutLocationType | ''>('');
  const [displayName, setDisplayName] = useState(
    value.length ? '' : (legacyDisplayName ?? ''),
  );
  const [searchValue, setSearchValue] = useState('');
  const [countryIso2, setCountryIso2] = useState('');
  const [error, setError] = useState<string | null>(null);

  const addLocation = () => {
    const normalizedDisplayName = displayName.trim();
    const normalizedValue = searchValue.trim() || normalizedDisplayName;
    const normalizedIso2 = countryIso2.trim().toUpperCase();

    if (!locationType) {
      setError('Choose a location type.');
      return;
    }
    if (!normalizedDisplayName || !normalizedValue) {
      setError('Enter a location name.');
      return;
    }
    if (normalizedIso2 && !/^[A-Z]{2}$/.test(normalizedIso2)) {
      setError('Country ISO2 must contain exactly two letters.');
      return;
    }

    const nextLocation = normalizeLocationTarget({
      displayName: normalizedDisplayName,
      type: locationType,
      value: normalizedValue,
      countryIso2: normalizedIso2 || null,
    });
    const nextKey = locationTargetKey(nextLocation);
    if (value.some((location) => locationTargetKey(location) === nextKey)) {
      setError('That location target is already allowed.');
      return;
    }

    onChange([...value, nextLocation]);
    setLocationType('');
    setDisplayName('');
    setSearchValue('');
    setCountryIso2('');
    setError(null);
  };

  const removeLocation = (location: ScoutLocationTarget) => {
    const key = locationTargetKey(location);
    onChange(value.filter((candidate) => locationTargetKey(candidate) !== key));
  };

  return (
    <div className="grid gap-3">
      <Label htmlFor={`${id}-name`}>Allowed locations</Label>
      {legacyDisplayName && value.length === 0 && (
        <Paragraph
          variant="caption"
          className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800"
        >
          Legacy location “{legacyDisplayName}” has no saved type. Choose its
          type before Scout can size or execute the search.
        </Paragraph>
      )}
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-type`}>Type</Label>
          <Select
            value={locationType}
            onValueChange={(nextValue) => {
              if (isLocationType(nextValue)) {
                setLocationType(nextValue);
                setError(null);
              }
            }}
            disabled={disabled}
          >
            <SelectTrigger id={`${id}-type`} className="w-full">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {LOCATION_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-name`}>Display name</Label>
          <Input
            id={`${id}-name`}
            value={displayName}
            onChange={(event) => {
              setDisplayName(event.target.value);
              setError(null);
            }}
            placeholder="e.g. Georgia"
            disabled={disabled}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-value`}>Search value</Label>
          <Input
            id={`${id}-value`}
            value={searchValue}
            onChange={(event) => {
              setSearchValue(event.target.value);
              setError(null);
            }}
            placeholder="Defaults to display name"
            disabled={disabled}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-iso2`}>Country ISO2 (optional)</Label>
          <Input
            id={`${id}-iso2`}
            value={countryIso2}
            onChange={(event) => {
              setCountryIso2(event.target.value.toUpperCase());
              setError(null);
            }}
            maxLength={2}
            placeholder="e.g. US"
            disabled={disabled}
          />
        </div>
      </div>
      <div className="flex items-center justify-between gap-3">
        {error ? (
          <Paragraph variant="caption" className="text-destructive">
            {error}
          </Paragraph>
        ) : (
          <Paragraph variant="caption">
            Identity uses type, search value, and ISO2. Scout never guesses or
            broadens geography.
          </Paragraph>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addLocation}
          disabled={disabled}
        >
          Add location
        </Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((location) => {
            const formattedLocation = formatLocationTarget(location);
            return (
              <Badge
                key={locationTargetKey(location)}
                variant="secondary"
                className="pr-1"
              >
                {formattedLocation}
                <button
                  type="button"
                  onClick={() => removeLocation(location)}
                  disabled={disabled}
                  aria-label={`Remove ${formattedLocation}`}
                  className="hover:bg-muted ml-1 cursor-pointer rounded-sm disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <XIcon className="size-3" aria-hidden />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ChampionFiltersPreview({
  filters,
  marketSize,
  marketStatus,
  sizingCurrent,
  targetMin,
  targetMax,
  appliedAdjustments,
  legacyLocationDisplay,
  draft,
  onApply,
  onReset,
  loading,
  disabled,
  error,
}: {
  filters?: ScoutChampionFilters | null;
  marketSize: number;
  marketStatus: ScoutMarketStatus;
  sizingCurrent: boolean;
  targetMin: number;
  targetMax: number;
  appliedAdjustments: string[];
  legacyLocationDisplay?: string | null;
  draft: ChampionFilterDraft;
  onApply: (draft: ChampionFilterDraft) => Promise<void>;
  onReset: () => ChampionFilterDraft | null;
  loading: boolean;
  disabled: boolean;
  error?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [localDraft, setLocalDraft] = useState(draft);
  const chips = chipsFromFilterDraft(draft);
  const tagInputClassName = 'space-y-2 [&>div]:space-y-2';
  const update = <K extends keyof ChampionFilterDraft>(
    key: K,
    value: ChampionFilterDraft[K],
  ) => setLocalDraft((currentDraft) => ({ ...currentDraft, [key]: value }));

  const onOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setLocalDraft(draft);
    }
    setOpen(nextOpen);
  };

  const onApplyClick = async () => {
    try {
      await onApply(localDraft);
      setOpen(false);
    } catch {
      // The parent action already surfaces the specific issue in the page.
    }
  };

  const onResetClick = () => {
    const resetDraft = onReset();
    if (resetDraft) {
      setLocalDraft(resetDraft);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <div>
        <div className="flex items-start justify-between gap-4 p-4">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold">
                Talent pool:{' '}
                {sizingCurrent
                  ? `${marketSize.toLocaleString()} profiles`
                  : 'refresh required'}
              </span>
              <span className="text-muted-foreground text-xs">
                ({filters?.groups?.[0]?.label ?? 'Search profile'})
              </span>
              <Badge variant="outline">
                {sizingCurrent
                  ? marketStatus === 'in_range'
                    ? 'In range'
                    : marketStatus === 'below_target'
                      ? 'Below target'
                      : marketStatus === 'above_target'
                        ? 'Above target'
                        : 'No market'
                  : 'Sizing required'}{' '}
                · target {targetMin.toLocaleString()}–
                {targetMax.toLocaleString()}
              </Badge>
              <span className="text-muted-foreground text-xs font-medium">
                {chips.length} active filters
              </span>
            </div>
            <div className="flex min-w-0 flex-wrap gap-1.5 overflow-hidden">
              {(chips.length > 0 ? chips : (filters?.chips ?? [])).map(
                (chip) => (
                  <Badge
                    key={`${chip.label}:${chip.value}`}
                    variant="outline"
                    className="max-w-full min-w-0 justify-start bg-slate-50 text-left text-[11px] whitespace-normal"
                  >
                    <span className="text-muted-foreground shrink-0">
                      {chip.label}:
                    </span>
                    <span className="min-w-0 break-words">{chip.value}</span>
                  </Badge>
                ),
              )}
            </div>
            {sizingCurrent && appliedAdjustments.length > 0 && (
              <Paragraph variant="caption">
                Scout adjusted: {appliedAdjustments.join(', ')}
              </Paragraph>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(true)}
            disabled={disabled}
          >
            <SlidersHorizontalIcon className="size-4" />
            Edit filters
          </Button>
        </div>
      </div>

      <SheetContent className="w-[720px] max-w-[calc(100vw-2rem)] sm:max-w-[720px]">
        <SheetHeader className="border-border border-b px-6 py-5">
          <SheetTitle>Edit candidate filters</SheetTitle>
          <SheetDescription>
            Tune the search criteria, then refresh the talent pool and candidate
            sample.
          </SheetDescription>
        </SheetHeader>

        <div className="divide-border flex-1 divide-y overflow-y-auto">
          <section className="space-y-4 px-6 py-5">
            <Subheading variant="h6" className="text-foreground">
              Identity & location
            </Subheading>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="scout-filter-current-title">
                  Current title
                </Label>
                <Input
                  id="scout-filter-current-title"
                  value={localDraft.baseTitle}
                  onChange={(event) => update('baseTitle', event.target.value)}
                  disabled={disabled}
                />
              </div>
              <div className="col-span-2">
                <LocationTargetsEditor
                  key={localDraft.locations.map(locationTargetKey).join('|')}
                  value={localDraft.locations}
                  legacyDisplayName={legacyLocationDisplay}
                  disabled={disabled}
                  onChange={(locations) => update('locations', locations)}
                />
              </div>
              <div className="col-span-2">
                <TagInput
                  label="Title variants"
                  value={localDraft.titleVariants}
                  onChange={(value) => update('titleVariants', value)}
                  placeholder="Add title variant + Enter"
                  className={tagInputClassName}
                  disabled={disabled}
                />
              </div>
              <div className="col-span-2">
                <TagInput
                  label="Headline keywords"
                  value={localDraft.headlineKeywords}
                  onChange={(value) => update('headlineKeywords', value)}
                  disabled={disabled}
                  placeholder="Add optional keyword + Enter"
                  className={tagInputClassName}
                />
              </div>
            </div>
          </section>

          <section className="space-y-4 px-6 py-5">
            <Subheading variant="h6" className="text-foreground">
              Role & seniority
            </Subheading>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="scout-filter-seniority">Seniority</Label>
                <Input
                  id="scout-filter-seniority"
                  value={localDraft.seniority}
                  onChange={(event) => update('seniority', event.target.value)}
                  disabled={disabled}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="scout-filter-management-level">
                  Management level
                </Label>
                <Input
                  id="scout-filter-management-level"
                  value={localDraft.managementLevel}
                  onChange={(event) =>
                    update('managementLevel', event.target.value)
                  }
                  disabled={disabled}
                />
              </div>
              <div className="col-span-2">
                <TagInput
                  label="Excluded current titles"
                  value={localDraft.excludedTitles}
                  onChange={(value) => update('excludedTitles', value)}
                  placeholder="Add title to exclude + Enter"
                  className={tagInputClassName}
                  disabled={disabled}
                />
              </div>
            </div>
          </section>

          <section className="space-y-4 px-6 py-5">
            <Subheading variant="h6" className="text-foreground">
              Skills & languages
            </Subheading>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <TagInput
                  label="Skills"
                  value={localDraft.skills}
                  onChange={(value) => update('skills', value)}
                  placeholder="Add skill + Enter"
                  className={tagInputClassName}
                  disabled={disabled}
                />
                <div className="grid gap-1.5">
                  <Label htmlFor="scout-filter-skill-match-mode">
                    Skill matching
                  </Label>
                  <Select
                    value={localDraft.skillMatchMode}
                    onValueChange={(value) => {
                      if (value === 'any' || value === 'all') {
                        update('skillMatchMode', value);
                      }
                    }}
                    disabled={disabled}
                  >
                    <SelectTrigger
                      id="scout-filter-skill-match-mode"
                      className="w-full"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Match any skill</SelectItem>
                      <SelectItem value="all">Match all skills</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <TagInput
                label="Languages"
                value={localDraft.languages}
                onChange={(value) => update('languages', value)}
                placeholder="Add language + Enter"
                className={tagInputClassName}
                disabled={disabled}
              />
            </div>
          </section>
        </div>

        <SheetFooter className="border-border bg-muted/30 border-t px-6 py-4">
          {error && (
            <Paragraph className="border-destructive/20 bg-destructive/5 text-destructive rounded-lg border px-3 py-2 text-sm">
              {error}
            </Paragraph>
          )}
          <Paragraph variant="caption" className="text-neutral-550">
            Applying changes refreshes the talent pool and replaces the
            candidate preview.
          </Paragraph>
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={onResetClick}
              disabled={disabled}
            >
              Reset filters
            </Button>
            <Button
              onClick={onApplyClick}
              disabled={disabled}
              loading={loading}
            >
              Apply & refresh preview
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

type WizardStage =
  | { type: 'prompt' }
  | { type: 'targeting'; prompt: string }
  | { type: 'review'; idealProfile: ScoutIdealProfile }
  | { type: 'in_flight' };

const FLOW_STEPS = [
  { id: 'brief', label: 'Brief' },
  { id: 'targeting', label: 'Targeting' },
  { id: 'review', label: 'Review' },
  { id: 'search', label: 'Search' },
] as const;

type FlowStepId = (typeof FLOW_STEPS)[number]['id'];

function getActiveFlowStep(stage: WizardStage): FlowStepId {
  if (stage.type === 'prompt') return 'brief';
  if (stage.type === 'targeting') return 'targeting';
  if (stage.type === 'review') return 'review';
  return 'search';
}

function normalizeSourcingState(
  state: string | number | null | undefined,
): 'Finished' | 'Failed' | 'InProgress' | null {
  if (state === 'Finished' || state === 4) return 'Finished';
  if (state === 'Failed' || state === 3) return 'Failed';
  if (state === 'InProgress' || state === 2) return 'InProgress';
  return null;
}

function getThinkingLine(
  stage: WizardStage,
  isBusy: boolean,
  sourcingState?: string | number | null,
) {
  const normalizedState = normalizeSourcingState(sourcingState);
  if (isBusy && stage.type === 'prompt') return 'Translating requirements...';
  if (isBusy && stage.type === 'targeting') {
    return 'Learning your preferences - generating the next examples...';
  }
  if (isBusy && stage.type === 'review')
    return 'Preparing the candidate preview...';
  if (stage.type === 'targeting') return 'Learning your preferences';
  if (stage.type === 'review') return 'Ready to preview candidates';
  if (stage.type === 'in_flight' && normalizedState === 'Finished') {
    return 'Search complete';
  }
  if (stage.type === 'in_flight' && normalizedState === 'Failed') {
    return 'Search failed';
  }
  if (stage.type === 'in_flight')
    return 'Building market, sourcing, and scoring';
  return '';
}

function SearchProgressPanel({
  phase,
}: {
  phase: 'filters' | 'market' | 'candidates';
}) {
  const titleByPhase = {
    filters: 'Building the talent pool',
    market: 'Checking market size',
    candidates: 'Loading candidate preview',
  };
  const descriptionByPhase = {
    filters:
      'Scout is testing search queries, measuring the talent pool, and selecting champion queries.',
    market:
      'Scout is validating that your edited filters still return candidates before sourcing starts.',
    candidates:
      'Scout is fetching preview profiles, ranking the strongest matches, and preparing the review list.',
  };
  const steps = [
    {
      label: 'Search diagnostics',
      active: phase === 'filters',
      done: phase === 'market' || phase === 'candidates',
    },
    {
      label: 'Market size',
      active: phase === 'market',
      done: phase === 'candidates',
    },
    {
      label: 'Candidate preview',
      active: phase === 'candidates',
      done: false,
    },
  ];

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardContent className="flex flex-col gap-4 px-4 py-4">
        <div className="flex items-start gap-3">
          <div className="bg-primary/10 text-primary mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full">
            <Loader2Icon className="size-4 animate-spin" aria-hidden />
          </div>
          <div className="min-w-0">
            <Subheading variant="h6" className="text-primary-700">
              {titleByPhase[phase]}
            </Subheading>
            <Paragraph variant="caption" className="mt-1 text-neutral-700">
              {descriptionByPhase[phase]}
            </Paragraph>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {steps.map((step) => (
            <span
              key={step.label}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium',
                step.active &&
                  'border-primary/30 text-primary bg-white shadow-sm',
                step.done &&
                  'border-emerald-200 bg-emerald-50 text-emerald-700',
                !step.active &&
                  !step.done &&
                  'text-neutral-650 border-neutral-200 bg-white/60',
              )}
            >
              {step.done ? (
                <CheckIcon className="size-3.5" />
              ) : step.active ? (
                <Loader2Icon className="size-3.5 animate-spin" />
              ) : null}
              {step.label}
            </span>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function FlowRail({ active }: { active: FlowStepId }) {
  const activeIndex = FLOW_STEPS.findIndex((step) => step.id === active);

  return (
    <ol className="border-border bg-card flex overflow-hidden rounded-lg border">
      {FLOW_STEPS.map((step, index) => {
        const done = index < activeIndex;
        const current = index === activeIndex;
        return (
          <li
            key={step.id}
            className={cn(
              'flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-xs font-semibold',
              current ? 'text-primary bg-secondary-150/50' : 'text-neutral-650',
            )}
          >
            <span
              className={cn(
                'flex size-5 shrink-0 items-center justify-center rounded-full border text-[10px]',
                done && 'bg-primary border-primary text-white',
                current && 'border-primary text-primary',
              )}
            >
              {done ? <CheckIcon className="size-3" /> : index + 1}
            </span>
            <span className="truncate">{step.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

function ActiveBrief({
  prompt,
  inquiry,
}: {
  prompt: string;
  inquiry: ScoutInquiry | null;
}) {
  if (!prompt.trim() && !inquiry) return null;

  const title = inquiry?.baseTitle || prompt;
  const activeInquiryLocations = inquiryLocations(inquiry);
  const legacyInquiryLocation =
    activeInquiryLocations.length === 0 ? inquiry?.location?.trim() : null;
  const details = [
    inquiry?.roleType,
    inquiry?.seniorityBand,
    inquiry?.resolvedCompanyIds?.length
      ? `${inquiry.resolvedCompanyIds.length} peer companies`
      : null,
  ].filter(Boolean);

  return (
    <div className="border-border bg-muted/30 rounded-lg border px-4 py-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <Paragraph className="text-foreground max-w-full truncate font-semibold">
          {title}
        </Paragraph>
        {details.length > 0 && (
          <Paragraph variant="caption">{details.join(' · ')}</Paragraph>
        )}
      </div>
      {inquiry?.lockedPromptCore && (
        <Paragraph variant="caption" className="mt-1 truncate">
          {inquiry.lockedPromptCore}
        </Paragraph>
      )}
      {(activeInquiryLocations.length > 0 || legacyInquiryLocation) && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {activeInquiryLocations.map((location) => (
            <Badge key={locationTargetKey(location)} variant="outline">
              {location.displayName}
            </Badge>
          ))}
          {legacyInquiryLocation && (
            <Badge variant="outline">
              {legacyInquiryLocation} · type required
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}

export function ScoutSourcingPage() {
  const { id: jobId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const resume = (location.state ?? null) as ScoutResumeState | null;

  const [stage, setStage] = useState<WizardStage>(() => {
    if (resume?.firstRound?.shouldStop && resume.firstRound.idealProfile) {
      return { type: 'review', idealProfile: resume.firstRound.idealProfile };
    }
    return resume ? { type: 'targeting', prompt: '' } : { type: 'prompt' };
  });
  const [prompt, setPrompt] = useState('');
  const [jobSourcingId, setJobSourcingId] = useState<string | null>(
    resume?.jobSourcingId ?? null,
  );
  const [inquiry, setInquiry] = useState<ScoutInquiry | null>(null);
  const [roundIndex, setRoundIndex] = useState(
    resume?.firstRound?.roundIndex ?? 0,
  );
  const [confidence, setConfidence] = useState(
    resume?.firstRound?.confidence ?? 0,
  );
  const [profiles, setProfiles] = useState<ScoutTargetingPersona[]>(
    resume?.firstRound?.generatedProfiles ?? [],
  );

  const startMutation = useStartScoutMutation();
  const nextMutation = useNextTargetingRoundMutation();
  const previewQueryMutation = usePreviewChampionQueryMutation();
  const previewCandidatesMutation = usePreviewCandidatesMutation();
  const previewMarketSizeMutation = usePreviewMarketSizeMutation();
  const confirmMutation = useConfirmTargetingMutation();
  const abandonMutation = useAbandonTargetingMutation();
  const scoutSessionQuery = useScoutTargetingSessionQuery(jobId ?? '');

  const jobQuery = useJobQuery(jobId ?? '', {
    enabled: Boolean(jobId) && stage.type !== 'prompt',
    refetchInterval: stage.type === 'in_flight' ? 2000 : false,
  });
  const sourcingState = jobQuery.data?.sourcingState;
  const normalizedSourcingState = normalizeSourcingState(sourcingState);
  // `calibrationLog` is streamed onto the in-progress sourcing; the generated job
  // schema may not type it yet, so read it through a narrow cast.
  const calibrationLog =
    (
      jobQuery.data?.lastSourcing as
        | { calibrationLog?: ScoutCalibrationLog | null }
        | undefined
    )?.calibrationLog ?? null;
  const [queryPreview, setQueryPreview] =
    useState<ScoutPreviewChampionQueryResponse | null>(null);
  const [candidatePreview, setCandidatePreview] =
    useState<ScoutPreviewCandidatesResponse | null>(null);
  const [marketSnapshot, setMarketSnapshot] = useState<MarketSnapshot | null>(
    null,
  );
  const [editedQueryError, setEditedQueryError] = useState<string | null>(null);
  const [outOfRangeDialogOpen, setOutOfRangeDialogOpen] = useState(false);
  const [filterDraft, setFilterDraft] =
    useState<ChampionFilterDraft>(EMPTY_FILTER_DRAFT);
  const autoPreviewInFlightRef = useRef<string | null>(null);
  const autoPreviewFailedRef = useRef<string | null>(null);

  useEffect(() => {
    const session = scoutSessionQuery.data;
    if (!session) {
      return;
    }

    let active = true;
    queueMicrotask(() => {
      if (!active) return;

      const latestRound = session.rounds.at(-1);
      setJobSourcingId(session.jobSourcingId);
      setInquiry(session.inquiry);
      setConfidence(session.confidence);
      if (session.championPreview && !queryPreview) {
        const snapshot = marketSnapshotFromPreview(
          session.championPreview,
          session.inquiry,
        );
        setQueryPreview(session.championPreview);
        setMarketSnapshot(snapshot);
        setFilterDraft(snapshot.searchCriteria);
        setCandidatePreview(null);
        setEditedQueryError(null);
        autoPreviewFailedRef.current = null;
      }

      if (session.status === 'Confirmed') {
        setStage({ type: 'in_flight' });
        return;
      }

      if (stage.type === 'in_flight') {
        return;
      }

      if (stage.type === 'review') {
        return;
      }

      if ((session.championPreview || queryPreview) && session.idealProfile) {
        setStage({ type: 'review', idealProfile: session.idealProfile });
        return;
      }

      if (session.idealProfile && session.canConfirm) {
        setStage({ type: 'review', idealProfile: session.idealProfile });
        return;
      }

      if (latestRound) {
        setRoundIndex(latestRound.roundIndex);
        setProfiles(latestRound.generatedProfiles);
        setStage({
          type: 'targeting',
          prompt: session.inquiry.originalPrompt ?? '',
        });
      }
    });

    return () => {
      active = false;
    };
  }, [queryPreview, scoutSessionQuery.data, stage.type]);

  useEffect(() => {
    if (
      stage.type !== 'review' ||
      !jobId ||
      !jobSourcingId ||
      queryPreview ||
      previewQueryMutation.isPending ||
      autoPreviewInFlightRef.current === jobSourcingId ||
      autoPreviewFailedRef.current === jobSourcingId
    ) {
      return;
    }

    const requestKey = jobSourcingId;
    autoPreviewInFlightRef.current = requestKey;

    previewQueryMutation
      .mutateAsync({ jobId, jobSourcingId })
      .then((preview) => {
        if (autoPreviewInFlightRef.current !== requestKey) return;
        const snapshot = marketSnapshotFromPreview(preview, inquiry);
        setQueryPreview(preview);
        setMarketSnapshot(snapshot);
        setFilterDraft(snapshot.searchCriteria);
        setCandidatePreview(null);
        setEditedQueryError(null);
        autoPreviewFailedRef.current = null;
      })
      .catch((e) => {
        if (autoPreviewInFlightRef.current !== requestKey) return;
        autoPreviewFailedRef.current = requestKey;
        setEditedQueryError(previewFiltersErrorMessage(e));
      })
      .finally(() => {
        if (autoPreviewInFlightRef.current === requestKey) {
          autoPreviewInFlightRef.current = null;
        }
      });
  }, [
    inquiry,
    jobId,
    jobSourcingId,
    previewQueryMutation,
    queryPreview,
    stage.type,
  ]);

  if (!jobId) {
    return (
      <Container>
        <Paragraph>No job id in URL.</Paragraph>
      </Container>
    );
  }

  const clearReviewPreview = () => {
    setQueryPreview(null);
    setCandidatePreview(null);
    setMarketSnapshot(null);
    setEditedQueryError(null);
    setOutOfRangeDialogOpen(false);
    setFilterDraft(EMPTY_FILTER_DRAFT);
    autoPreviewInFlightRef.current = null;
    autoPreviewFailedRef.current = null;
  };

  const onStart = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a brief or paste a job description.');
      return;
    }
    try {
      const res = await startMutation.mutateAsync({ jobId, prompt });
      setJobSourcingId(res.jobSourcingId);
      setInquiry(res.inquiry);
      setStage({ type: 'targeting', prompt });
      // Immediately request round 0.
      const round = await nextMutation.mutateAsync({
        jobId,
        jobSourcingId: res.jobSourcingId,
        votesForPreviousRound: null,
      });
      setRoundIndex(round.roundIndex);
      setConfidence(round.confidence);
      setProfiles(round.generatedProfiles);
      if (round.shouldStop && round.idealProfile) {
        setStage({ type: 'review', idealProfile: round.idealProfile });
        clearReviewPreview();
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to start Scout');
    }
  };

  const onSubmitVotes = async (votes: PersonaVoteRecord[]) => {
    if (!jobSourcingId) return;
    try {
      const round = await nextMutation.mutateAsync({
        jobId,
        jobSourcingId,
        votesForPreviousRound: votes,
      });
      setRoundIndex(round.roundIndex);
      setConfidence(round.confidence);
      setProfiles(round.generatedProfiles);
      if (round.shouldStop && round.idealProfile) {
        setStage({ type: 'review', idealProfile: round.idealProfile });
        clearReviewPreview();
      }
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : 'Failed to advance targeting',
      );
    }
  };

  const checkMarketSizeForCriteria = async (
    searchCriteria: ScoutSearchCriteria,
  ) => {
    if (!jobSourcingId || !queryPreview) {
      throw new Error('Search details are not ready yet.');
    }

    let result: ScoutMarketSizeResponse;
    try {
      setEditedQueryError(null);
      result = await previewMarketSizeMutation.mutateAsync({
        jobId,
        jobSourcingId,
        searchCriteria,
      });
    } catch (e) {
      const message =
        e instanceof Error
          ? e.message
          : 'Failed to update the talent pool size';
      setEditedQueryError(message);
      throw e;
    }

    const snapshot = marketSnapshotFromResponse(
      result,
      searchCriteria,
      marketSnapshot ?? marketSnapshotFromPreview(queryPreview, inquiry),
    );
    setMarketSnapshot(snapshot);
    setFilterDraft(snapshot.searchCriteria);
    if (snapshot.marketStatus === 'no_market') {
      const message =
        'No profiles match these titles and allowed locations. Change the titles or locations before sourcing.';
      setEditedQueryError(message);
      throw new Error(message);
    }
    toast.success(
      `Talent pool: ${result.marketSize.toLocaleString()} profiles`,
    );
    return snapshot;
  };

  const onApplyFilterDraft = async (draft: ChampionFilterDraft) => {
    if (!queryPreview || !jobSourcingId) return;
    const normalizedDraft = withSearchCriteriaDefaults(draft);
    const validationError = validateFilterDraft(normalizedDraft);
    if (validationError) {
      setEditedQueryError(validationError);
      toast.error(validationError);
      throw new Error(validationError);
    }

    setFilterDraft(normalizedDraft);
    setCandidatePreview(null);

    try {
      await checkMarketSizeForCriteria(normalizedDraft);
      const preview = await previewCandidatesMutation.mutateAsync({
        jobId,
        jobSourcingId,
        maxCandidates: scoutDefaults.rankedPreviewCandidateCap,
      });
      setCandidatePreview(preview);
      toast.success('Candidate preview refreshed');
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Failed to refresh candidate preview.';
      setEditedQueryError(message);
      toast.error(message);
      throw e;
    }
  };

  const onResetFilterDraft = () => {
    if (!queryPreview) return null;
    const snapshot = marketSnapshotFromPreview(queryPreview, inquiry);
    setMarketSnapshot(null);
    setFilterDraft(snapshot.searchCriteria);
    setEditedQueryError(null);
    setCandidatePreview(null);
    return snapshot.searchCriteria;
  };

  const applyQueryPreview = (preview: ScoutPreviewChampionQueryResponse) => {
    const snapshot = marketSnapshotFromPreview(preview, inquiry);
    setQueryPreview(preview);
    setMarketSnapshot(snapshot);
    setFilterDraft(snapshot.searchCriteria);
    setEditedQueryError(null);
    autoPreviewFailedRef.current = null;
  };

  const onPreviewFilters = async () => {
    if (!jobSourcingId) return;

    try {
      autoPreviewFailedRef.current = null;
      setEditedQueryError(null);
      const preview = await previewQueryMutation.mutateAsync({
        jobId,
        jobSourcingId,
      });
      applyQueryPreview(preview);
      setCandidatePreview(null);
      toast.success('Filters ready to review');
    } catch (e) {
      autoPreviewFailedRef.current = jobSourcingId;
      const message = previewFiltersErrorMessage(e);
      setEditedQueryError(message);
      toast.error(message);
    }
  };

  const onPreviewCandidates = async () => {
    if (!jobSourcingId || !queryPreview) return;

    try {
      setEditedQueryError(null);
      const validationError = validateFilterDraft(filterDraft);
      if (validationError) {
        setEditedQueryError(validationError);
        toast.error(validationError);
        return;
      }
      let activeSnapshot = marketSnapshot;
      if (
        !activeSnapshot ||
        normalizeSearchCriteria(activeSnapshot.searchCriteria) !==
          normalizeSearchCriteria(filterDraft)
      ) {
        activeSnapshot = await checkMarketSizeForCriteria(filterDraft);
      }
      if (activeSnapshot.marketStatus === 'no_market') return;

      const preview = await previewCandidatesMutation.mutateAsync({
        jobId,
        jobSourcingId,
        maxCandidates: scoutDefaults.rankedPreviewCandidateCap,
      });
      setCandidatePreview(preview);
      if (preview.candidateCount <= 0) {
        const message =
          'Candidate preview found no candidates for these filters. Broaden the search before starting sourcing.';
        setEditedQueryError(message);
        toast.error(message);
        return;
      }
      toast.success('Candidate preview ready');
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Failed to preview candidates';
      setEditedQueryError(message);
      toast.error(message);
    }
  };

  const confirmSourcing = async (acceptOutOfRangeMarket = false) => {
    if (!jobSourcingId || !queryPreview) return;
    const queryHasSizing =
      validateFilterDraft(filterDraft) === null &&
      marketSnapshot !== null &&
      normalizeSearchCriteria(marketSnapshot.searchCriteria) ===
        normalizeSearchCriteria(filterDraft);
    if (!queryHasSizing) {
      const message =
        'Refresh the talent pool and candidate preview before starting sourcing.';
      setEditedQueryError(message);
      toast.error(message);
      return;
    }

    if (marketSnapshot.marketStatus === 'no_market') {
      const message =
        'No profiles match these titles and allowed locations. Change the titles or locations before sourcing.';
      setEditedQueryError(message);
      toast.error(message);
      return;
    }

    if (!candidatePreview || candidatePreview.candidateCount <= 0) {
      const message =
        'Preview candidates and confirm the filters return candidates before starting sourcing.';
      setEditedQueryError(message);
      toast.error(message);
      return;
    }

    try {
      setEditedQueryError(null);
      await confirmMutation.mutateAsync({
        jobId,
        jobSourcingId,
        maxCandidates: scoutDefaults.rankedPreviewCandidateCap,
        acceptOutOfRangeMarket,
      });
      setOutOfRangeDialogOpen(false);
      setStage({ type: 'in_flight' });
      toast.success('Scout sourcing started');
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Failed to start sourcing';
      setEditedQueryError(message);
      toast.error(message);
    }
  };

  const onStartSourcing = () => {
    if (
      marketSnapshot?.marketStatus === 'below_target' ||
      marketSnapshot?.marketStatus === 'above_target'
    ) {
      setOutOfRangeDialogOpen(true);
      return;
    }
    void confirmSourcing();
  };

  const onStartOver = async () => {
    if (!jobSourcingId) {
      setStage({ type: 'prompt' });
      return;
    }
    try {
      await abandonMutation.mutateAsync({ jobId, jobSourcingId });
      setStage({ type: 'prompt' });
      setJobSourcingId(null);
      setProfiles([]);
      clearReviewPreview();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to reset targeting');
    }
  };

  const isBusy =
    startMutation.isPending ||
    nextMutation.isPending ||
    previewQueryMutation.isPending ||
    previewMarketSizeMutation.isPending ||
    previewCandidatesMutation.isPending ||
    confirmMutation.isPending ||
    abandonMutation.isPending;

  const activePrompt =
    inquiry?.originalPrompt ||
    (stage.type === 'targeting' ? stage.prompt : prompt);
  const activeStep = getActiveFlowStep(stage);
  const thinkingLine = getThinkingLine(stage, isBusy, sourcingState);
  const filterDraftIsExecutable = validateFilterDraft(filterDraft) === null;
  const queryHasSizing =
    Boolean(queryPreview) &&
    filterDraftIsExecutable &&
    marketSnapshot !== null &&
    normalizeSearchCriteria(marketSnapshot.searchCriteria) ===
      normalizeSearchCriteria(filterDraft);
  const querySizingLabel = (() => {
    if (!queryPreview) return null;
    if (!queryHasSizing || !marketSnapshot) return 'Sizing required';
    if (marketSnapshot.marketStatus === 'no_market') return 'No market';
    if (marketSnapshot.marketStatus === 'below_target') return 'Below target';
    if (marketSnapshot.marketStatus === 'above_target') return 'Above target';
    return 'In range';
  })();
  const querySizingClassName =
    querySizingLabel === 'In range'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : querySizingLabel === 'Above target'
        ? 'border-amber-200 bg-amber-50 text-amber-700'
        : querySizingLabel === 'Below target' ||
            querySizingLabel === 'No market'
          ? 'border-red-200 bg-red-50 text-red-700'
          : 'border-slate-200 bg-slate-50 text-slate-700';
  const displayedMarketSnapshot =
    marketSnapshot ??
    (queryPreview ? marketSnapshotFromPreview(queryPreview, inquiry) : null);
  const candidatePreviewHasCandidates =
    Boolean(candidatePreview) && candidatePreview!.candidateCount > 0;
  const queryCanStartSourcing =
    queryHasSizing &&
    marketSnapshot?.marketStatus !== 'no_market' &&
    candidatePreviewHasCandidates;
  const startSourcingFeedback = (() => {
    if (!queryPreview) return null;
    if (!queryHasSizing) {
      return 'Refresh the talent pool and candidate preview before starting sourcing.';
    }
    if (marketSnapshot?.marketStatus === 'no_market') {
      return 'No profiles match these titles and allowed locations. Change the titles or locations before sourcing.';
    }
    if (candidatePreview && candidatePreview.candidateCount <= 0) {
      return 'Candidate preview found no candidates for these filters. Broaden the search before starting sourcing.';
    }
    if (!candidatePreview) {
      return 'Preview candidates before starting sourcing.';
    }
    if (marketSnapshot && marketSnapshot.marketStatus !== 'in_range') {
      return `This pool is outside the target range of ${marketSnapshot.targetMin.toLocaleString()}–${marketSnapshot.targetMax.toLocaleString()}. Starting requires explicit confirmation.`;
    }
    return null;
  })();
  const previewStatusLabel = (() => {
    if (previewQueryMutation.isPending) {
      return queryPreview
        ? 'Refreshing talent-pool filters...'
        : 'Preparing talent-pool filters...';
    }
    if (previewMarketSizeMutation.isPending) {
      return 'Checking talent-pool size...';
    }
    if (previewCandidatesMutation.isPending) {
      return 'Loading candidate preview...';
    }
    return null;
  })();
  const previewProgressPhase = previewQueryMutation.isPending
    ? 'filters'
    : previewMarketSizeMutation.isPending
      ? 'market'
      : previewCandidatesMutation.isPending
        ? 'candidates'
        : null;
  const pageTitle =
    stage.type === 'prompt'
      ? 'Scout sourcing'
      : inquiry?.baseTitle || 'Scout sourcing';
  const pageSubtitle = jobQuery.data?.company ? (
    <Link
      to={`/companies/${jobQuery.data.company.id}/edit`}
      className="hover:underline"
    >
      {jobQuery.data.company.name}
    </Link>
  ) : thinkingLine ? (
    <span className="flex items-center gap-2 italic">
      {isBusy && <Loader2Icon className="size-3.5 animate-spin" />}
      {thinkingLine}
    </span>
  ) : undefined;
  const pageAction =
    stage.type !== 'prompt' && stage.type !== 'in_flight' ? (
      <Button
        variant="ghost"
        size="sm"
        onClick={onStartOver}
        disabled={isBusy}
        loading={abandonMutation.isPending}
      >
        Start over
      </Button>
    ) : undefined;

  return (
    <Container>
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
        <PageHeader
          title={pageTitle}
          subtitle={pageSubtitle}
          action={pageAction}
        />

        <FlowRail active={activeStep} />
        {stage.type !== 'prompt' && (
          <ActiveBrief prompt={activePrompt} inquiry={inquiry} />
        )}

        {stage.type === 'prompt' && (
          <div className="flex flex-col gap-3">
            <ScoutPromptInput
              value={prompt}
              onChange={setPrompt}
              onSubmit={onStart}
              loading={startMutation.isPending || nextMutation.isPending}
            />
            <Paragraph variant="caption" className="text-center">
              Describe the role in one sentence — the pills tick as criteria are
              detected.
            </Paragraph>
          </div>
        )}

        {stage.type === 'targeting' && (
          <div className="flex flex-col gap-4">
            <TargetingProgress
              roundIndex={roundIndex + 1}
              confidence={confidence}
            />
            <TargetingRound
              key={roundIndex}
              profiles={profiles}
              onSubmitVotes={onSubmitVotes}
              disabled={isBusy}
              loading={nextMutation.isPending}
            />
          </div>
        )}

        {stage.type === 'review' && (
          <div className="flex flex-col gap-4">
            <IdealProfileSummary
              idealProfile={stage.idealProfile}
              onPreviewCandidates={
                queryPreview ? onPreviewCandidates : onPreviewFilters
              }
              onAbandon={onStartOver}
              disabled={isBusy}
              previewLoading={
                previewQueryMutation.isPending ||
                previewCandidatesMutation.isPending ||
                previewMarketSizeMutation.isPending
              }
              abandonLoading={abandonMutation.isPending}
              previewButtonLabel={
                queryPreview
                  ? candidatePreview
                    ? 'Refresh preview'
                    : 'Preview candidates'
                  : 'Retry filters'
              }
              previewStatusLabel={previewStatusLabel}
              previewActionHidden={!queryPreview && !editedQueryError}
              hasCandidatePreview={Boolean(candidatePreview)}
              talentPoolSummary={
                queryPreview && displayedMarketSnapshot ? (
                  <ChampionFiltersPreview
                    filters={queryPreview.championFilters}
                    marketSize={displayedMarketSnapshot.marketSize}
                    marketStatus={displayedMarketSnapshot.marketStatus}
                    sizingCurrent={queryHasSizing}
                    targetMin={displayedMarketSnapshot.targetMin}
                    targetMax={displayedMarketSnapshot.targetMax}
                    appliedAdjustments={
                      displayedMarketSnapshot.appliedAdjustments
                    }
                    legacyLocationDisplay={legacyLocationDisplayFromPreview(
                      queryPreview,
                      inquiry,
                    )}
                    draft={filterDraft}
                    onApply={onApplyFilterDraft}
                    onReset={onResetFilterDraft}
                    loading={
                      previewMarketSizeMutation.isPending ||
                      previewCandidatesMutation.isPending
                    }
                    disabled={isBusy}
                    error={editedQueryError}
                  />
                ) : undefined
              }
            />
            {previewProgressPhase && (
              <SearchProgressPanel phase={previewProgressPhase} />
            )}
            {editedQueryError && (
              <Paragraph className="border-destructive/20 bg-destructive/5 text-destructive rounded-lg border px-4 py-3">
                {editedQueryError}
              </Paragraph>
            )}
            {marketSnapshot?.marketStatus === 'no_market' && (
              <div className="border-destructive/20 bg-destructive/5 flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3">
                {!editedQueryError && (
                  <Paragraph className="text-destructive">
                    No profiles match these titles and allowed locations. Change
                    the titles or locations before sourcing.
                  </Paragraph>
                )}
                <Button disabled>Start sourcing</Button>
              </div>
            )}
            {candidatePreview && (
              <Card>
                <CardHeader>
                  <div className="flex flex-wrap items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <CardTitle>Candidate preview</CardTitle>
                      <CardDescription>
                        Showing the top {candidatePreview.candidates.length} of{' '}
                        {Math.min(
                          candidatePreview.candidateCount,
                          scoutDefaults.rankedPreviewCandidateCap,
                        )}{' '}
                        ranked preview candidates. Start sourcing to save up to
                        the best {scoutDefaults.rankedPreviewCandidateCap} and
                        collect and shortlist the top{' '}
                        {scoutDefaults.defaultMaxCandidates}.
                      </CardDescription>
                    </div>
                    <Button
                      className="ml-auto shrink-0"
                      onClick={onStartSourcing}
                      disabled={isBusy || !queryCanStartSourcing}
                      loading={confirmMutation.isPending}
                    >
                      Start sourcing
                    </Button>
                  </div>
                  {startSourcingFeedback && (
                    <Paragraph
                      variant="caption"
                      className="text-muted-foreground"
                    >
                      {startSourcingFeedback}
                    </Paragraph>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  {candidatePreview.candidates.length === 0 ? (
                    <Paragraph className="border-destructive/20 bg-destructive/5 text-destructive rounded-lg border px-4 py-3">
                      Candidate preview found no candidates. Broaden the filters
                      and preview again before starting sourcing.
                    </Paragraph>
                  ) : (
                    candidatePreview.candidates.map((candidate) => (
                      <CandidateCard key={candidate.id} animate={false}>
                        <CandidateCardHeader>
                          <CandidateCardIdentity
                            name={candidate.fullName || 'Candidate'}
                            headline={
                              [candidate.currentTitle, candidate.currentCompany]
                                .filter(Boolean)
                                .join(' · ') || 'No current role available'
                            }
                            location={candidate.location}
                            linkedinUrl={candidate.linkedinUrl}
                            badge={<Badge variant="outline">Preview</Badge>}
                          />
                          <CandidateCardActions>
                            <CandidateScore
                              score={Math.round(candidate.scoutScore)}
                              size={16}
                            />
                          </CandidateCardActions>
                        </CandidateCardHeader>
                        <Separator />
                        <CandidateCardContent>
                          <div className="flex flex-col gap-3">
                            {candidate.headline && (
                              <Paragraph variant="caption">
                                {candidate.headline}
                              </Paragraph>
                            )}
                            {candidate.reason && (
                              <Paragraph variant="caption">
                                {candidate.reason}
                              </Paragraph>
                            )}
                            {candidate.aiScore != null && (
                              <Paragraph
                                variant="caption"
                                className="text-neutral-650"
                              >
                                AI score {candidate.aiScore}
                              </Paragraph>
                            )}
                          </div>
                        </CandidateCardContent>
                      </CandidateCard>
                    ))
                  )}
                </CardContent>
              </Card>
            )}
            {queryPreview && (
              <details className="group rounded-lg border bg-white">
                <summary className="hover:bg-secondary-150/40 flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold">
                      Search diagnostics
                    </span>
                    <Badge variant="outline" className={querySizingClassName}>
                      {querySizingLabel}
                    </Badge>
                    <Badge variant="outline">
                      {!queryHasSizing || marketSnapshot == null
                        ? 'pool not checked'
                        : `${marketSnapshot.marketSize.toLocaleString()} profiles`}
                    </Badge>
                  </div>
                  <ChevronDownIcon className="size-4 transition-transform group-open:rotate-180" />
                </summary>
                <div className="border-border flex flex-col gap-3 border-t p-3">
                  {queryHasSizing && marketSnapshot && (
                    <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
                      <Paragraph className="font-semibold text-emerald-900">
                        Current executable search
                      </Paragraph>
                      {marketSnapshot.championQueries.length > 0 ? (
                        <ul className="mt-2 space-y-1.5">
                          {marketSnapshot.championQueries.map(
                            (championQuery, index) => (
                              <li key={`${index}:${championQuery}`}>
                                <code className="block rounded bg-white px-2.5 py-1.5 text-xs whitespace-pre-wrap text-emerald-950">
                                  {championQuery}
                                </code>
                              </li>
                            ),
                          )}
                        </ul>
                      ) : (
                        <Paragraph variant="caption" className="mt-1">
                          The latest server-sized structured criteria are saved
                          for candidate preview and sourcing.
                        </Paragraph>
                      )}
                    </div>
                  )}
                  <CalibrationLog
                    log={queryPreview.calibrationLog}
                    championQueries={queryPreview.championQueries}
                    hideMarketSummary
                    historical
                  />
                  {!queryHasSizing && (
                    <Paragraph variant="caption">
                      Apply filters or preview candidates to refresh the talent
                      pool size before starting sourcing.
                    </Paragraph>
                  )}
                </div>
              </details>
            )}
          </div>
        )}

        {stage.type === 'in_flight' && (
          <div className="flex flex-col gap-4">
            {calibrationLog ? (
              <CalibrationLog log={calibrationLog} />
            ) : (
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2.5">
                    <span className="relative flex size-2.5">
                      <span className="bg-primary absolute inline-flex size-full animate-ping rounded-full opacity-60" />
                      <span className="bg-primary relative inline-flex size-2.5 rounded-full" />
                    </span>
                    <CardTitle>Scout is searching…</CardTitle>
                  </div>
                  <CardDescription>
                    We&apos;re ranking matches, saving up to the best{' '}
                    {scoutDefaults.rankedPreviewCandidateCap}, and collecting
                    and shortlisting the top{' '}
                    {scoutDefaults.defaultMaxCandidates}. They&apos;ll appear in
                    your candidate list as they come in.
                  </CardDescription>
                </CardHeader>
              </Card>
            )}

            {normalizedSourcingState === 'Finished' && (
              <Card>
                <CardHeader>
                  <CardTitle>Candidates are ready</CardTitle>
                  <CardDescription>
                    Scout saved up to the best{' '}
                    {scoutDefaults.rankedPreviewCandidateCap} ranked preview
                    candidates and collected and shortlisted the top{' '}
                    {scoutDefaults.defaultMaxCandidates}. Review the
                    market-sizing queries above, then open the list when ready.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex justify-end">
                  <Button onClick={() => navigate(`/jobs/${jobId}/candidates`)}>
                    View candidates
                  </Button>
                </CardContent>
              </Card>
            )}

            {normalizedSourcingState === 'Failed' && (
              <Card>
                <CardHeader>
                  <CardTitle>Scout couldn&apos;t finish</CardTitle>
                  <CardDescription>
                    The search didn&apos;t complete. Review the market-sizing
                    queries above, then try running it again.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex justify-end">
                  <Button onClick={() => setStage({ type: 'prompt' })}>
                    Try again
                  </Button>
                </CardContent>
              </Card>
            )}

            {normalizedSourcingState !== 'Finished' &&
              normalizedSourcingState !== 'Failed' && (
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/jobs/${jobId}/candidates`)}
                  >
                    Watch candidates arrive
                  </Button>
                </div>
              )}
          </div>
        )}
        <AlertDialog
          open={outOfRangeDialogOpen}
          onOpenChange={setOutOfRangeDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Start sourcing outside the target?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This search currently contains{' '}
                {marketSnapshot?.marketSize.toLocaleString() ?? 0} profiles. The
                target range is{' '}
                {marketSnapshot?.targetMin.toLocaleString() ?? 50}–
                {marketSnapshot?.targetMax.toLocaleString() ?? 300}. Scout will
                keep every allowed location unchanged.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={confirmMutation.isPending}>
                Keep editing
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(event) => {
                  event.preventDefault();
                  void confirmSourcing(true);
                }}
                disabled={confirmMutation.isPending}
              >
                Start sourcing anyway
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Container>
  );
}
